import type { Action } from "svelte/action"

/** Keep keyboard focus in the active overlay and return it to its opener. */
export const containOverlayFocus: Action<HTMLElement, boolean> = (node, enabled) => {
  let dispose = () => {}
  const activate = (active: boolean) => {
    dispose()

    if (!active) {
      dispose = () => {}

      return
    }

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    let disposed = false
    const focusable = () =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          !element.closest('[inert], [aria-hidden="true"]') && element.getClientRects().length > 0,
      )
    const focusFirst = () => (focusable()[0] ?? node).focus()
    const keydown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || document.querySelector("dialog[open]")) return

      const choices = focusable()
      const first = choices[0],
        last = choices[choices.length - 1]

      if (
        !first ||
        (!event.shiftKey && document.activeElement === last) ||
        (event.shiftKey && (document.activeElement === first || document.activeElement === node))
      ) {
        event.preventDefault()
        ;(event.shiftKey ? (last ?? node) : (first ?? node)).focus()
      }
    }
    const focusin = (event: FocusEvent) => {
      if (
        !document.querySelector("dialog[open]") &&
        event.target instanceof Node &&
        !node.contains(event.target)
      )
        focusFirst()
    }
    document.addEventListener("keydown", keydown)
    document.addEventListener("focusin", focusin)
    queueMicrotask(() => {
      if (!disposed && !node.contains(document.activeElement)) focusFirst()
    })
    dispose = () => {
      disposed = true
      document.removeEventListener("keydown", keydown)
      document.removeEventListener("focusin", focusin)

      if (opener?.isConnected && !node.contains(opener)) opener.focus()
    }
  }
  activate(enabled)

  return { update: activate, destroy: () => dispose() }
}
